/**
 * The toolbar tile (design `04h Live · controls states`), moved out of Toolbar
 * so the footer's tiles and any future tile grid share one body.
 *
 *   tile      height 60, radius 12, 1.5 px ring, bg `well`, gap 5, padding 0 4
 *   label     12/14 semibold, nowrap
 *   ½ glyph   20/700, line-height 18, box 18
 *   icon      18, 2 px stroke
 *   default   ring transparent, text `ink2`   (resign: `danger`)
 *   hover     bg `surface` + sh1               (markup: style-hover)
 *   active    ring `ev`, bg `evTint`, text `ev`  ("Offered · waiting")
 *   holding   bg `dangerTint`, label "Hold…"
 *   disabled  opacity 0.4
 */
import React from 'react';
import {Pressable, StyleSheet} from 'react-native';

import Icon, {IconName} from '../components/Icon';
import {MOTION, RADIUS, SIZE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import Txt from './Txt';

/** 04h: the tile grid */
export const TILE_HEIGHT = SIZE.tile;
export const TILE_RADIUS = RADIUS.control;
export const TILE_RING = 1.5;
/** the press step of the markup's `style-press` */
const PRESS_SCALE = 0.985;
const DISABLED_OPACITY = 0.4;

export type TileTone = 'default' | 'danger' | 'active' | 'holding';

export type TileProps = {
  icon?: IconName;
  /** the ½ of the draw tile, which is type and not an icon */
  glyph?: string;
  label: string;
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  tone?: TileTone;
  disabled?: boolean;
  children?: React.ReactNode;
};

function Tile({
  icon,
  glyph,
  label,
  onPress,
  onPressIn,
  onPressOut,
  tone = 'default',
  disabled,
  children,
}: TileProps) {
  const tokens = useV2Theme(s => s.tokens);

  const color =
    tone === 'danger' || tone === 'holding'
      ? tokens.danger
      : tone === 'active'
      ? tokens.ev
      : tokens.ink2;

  const background =
    tone === 'active'
      ? tokens.evTint
      : tone === 'holding'
      ? tokens.dangerTint
      : tokens.well;

  const ring = tone === 'active' ? tokens.ev : 'transparent';

  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={({hovered, pressed}: any) => [
        styles.tile,
        {
          backgroundColor:
            hovered && !disabled && tone === 'default'
              ? tokens.surface
              : background,
          borderColor: ring,
          boxShadow:
            hovered && !disabled
              ? tokens.sh1
              : `inset 0 0 0 1px ${tokens.line}`,
          opacity: disabled ? DISABLED_OPACITY : 1,
          transform: pressed && !disabled ? [{scale: PRESS_SCALE}] : undefined,
          transitionProperty:
            'box-shadow, background-color, opacity, transform',
          transitionDuration: `${MOTION.ms}ms`,
          transitionTimingFunction: MOTION.easing,
        } as any,
      ]}>
      {children ??
        (glyph ? (
          <Txt variant="glyph" color={color} style={styles.glyph}>
            {glyph}
          </Txt>
        ) : (
          <Icon name={icon as IconName} color={color} />
        ))}
      <Txt variant="tileLabel" color={color} numberOfLines={1}>
        {label}
      </Txt>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tile: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: SIZE.tile,
    height: TILE_HEIGHT,
    borderRadius: TILE_RADIUS,
    borderWidth: TILE_RING,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  glyph: {
    height: 18,
  },
});

export default React.memo(Tile);
